import type { LearningResource } from "@peer-start/platform-contracts";
import catheterCare from "../../../assets/learning/healthcare-lesson-still.jpg";
import catheterRinsing from "../../../assets/learning/bks-video-still.jpg";
import diabetes from "../../../assets/learning/dbm-video-still.jpg";
import woundCare from "../../../assets/learning/bwz-video-still.jpg";
import dementia from "../../../assets/learning/dem-video-still.jpg";
import handHygiene from "../../../assets/learning/hhy-video-still-v2.jpg";
import endOfLife from "../../../assets/learning/zrs-video-still.jpg";
import healthyBehaviour from "../../../assets/learning/ggs-video-still.jpg";
import artificialIntelligence from "../../../assets/learning/aiz-video-still.jpg";
import type {
  ExtendedLearningCourse,
  LearningPresentation,
  LearningVimeoVideo,
} from "../types/learning-presentation.js";

// Presentation-only mock content, keyed by stable course identity rather than list position.
// Each video has its own still. New courses fall back to text until a format is curated.
export const learningVideoStills: Readonly<Record<string, string>> = {
  "vig-course-vvk": catheterCare,
  "vig-course-bks": catheterRinsing,
  "vig-course-dbm": diabetes,
  "vig-course-bwz": woundCare,
  "vig-course-dem": dementia,
  "vig-course-hhy": handHygiene,
  "vig-course-zrs": endOfLife,
  "vig-course-ggs": healthyBehaviour,
  "vig-course-aiz": artificialIntelligence,
};

type LearningTopicMedia = {
  vimeoVideos: readonly LearningVimeoVideo[];
  extendedCourses: readonly ExtendedLearningCourse[];
};

// Media is attached to stable trainable-topic IDs. Add one or more entries per topic.
export const learningMediaByTopic: Readonly<Record<string, LearningTopicMedia>> = {
  "vig-atom-inj-v04": {
    vimeoVideos: [
      {
        videoId: "1226951538",
        startSeconds: 7,
        endSeconds: 14,
        thumbnailSrc:
          "https://i.vimeocdn.com/video/2201060581-1e5dd45844edddc1db3ab5bfe21d48981fb877e3091e35b55b964edd1494fc26-d_295x166?region=us",
      },
    ],
    extendedCourses: [
      {
        id: "cme-medicatie-toedienen",
        title: "Medicatie toedienen",
        provider: "CME-Online",
        href: "https://www.cme-online.nl/cursus/medicatie-toedienen-1412486/",
        durationMinutes: 120,
      },
    ],
  },
};

const visualCourses = new Set([
  "act",
  "bkm",
  "dii",
  "imi",
  "vni",
  "khk",
  "peg",
  "vms",
  "sto",
  "dec",
  "zto",
  "vfm",
  "mvk",
  "mre",
  "oui",
  "cva",
  "nah",
  "sli",
  "psk",
  "del",
  "pij",
  "pbm",
  "vtc",
  "val",
  "elh",
  "mhg",
  "wzz",
  "ppb",
  "pzp",
  "ztg",
  "isz",
  "soap",
  "smz",
  "inc",
]);

export function learningPresentation(
  resource: Pick<LearningResource, "id" | "criterionIds" | "sections">,
): LearningPresentation {
  const sectionVideos = resource.sections.flatMap((section) =>
    section.video?.provider === "vimeo" ? [section.video] : [],
  );
  const imageSrc = learningVideoStills[resource.id];
  if (sectionVideos.length)
    return { format: "video", imageSrc, vimeoVideos: sectionVideos, extendedCourses: [] };
  const media = resource.criterionIds.flatMap((topicId) => {
    const topicMedia = learningMediaByTopic[topicId];
    return topicMedia ? [topicMedia] : [];
  });
  const vimeoVideos = [
    ...new Map(media.flatMap((topic) => topic.vimeoVideos).map((video) => [video.videoId, video])).values(),
  ];
  const extendedCourses = [
    ...new Map(
      media.flatMap((topic) => topic.extendedCourses).map((course) => [course.href, course]),
    ).values(),
  ];
  if (vimeoVideos.length || imageSrc) return { format: "video", imageSrc, vimeoVideos, extendedCourses };
  return {
    format: visualCourses.has(resource.id.replace(/^vig-course-/, "")) ? "visual" : "text",
  };
}
